// Custom hooks for Cigar Journal
import { useState, useEffect, useCallback } from 'react';
import { User, TastingNote, Share, Activity, ApiResponse } from '../types';

const API_BASE = '/api';

// Get auth token from localStorage
const getToken = (): string | null => localStorage.getItem('cigar_journal_token');
const setToken = (token: string): void => localStorage.setItem('cigar_journal_token', token);
const clearToken = (): void => localStorage.removeItem('cigar_journal_token');

// Guest mode helpers
const isGuestMode = (): boolean => localStorage.getItem('cigar_journal_guest') === 'true';
const setGuestMode = (value: boolean): void => {
  if (value) {
    localStorage.setItem('cigar_journal_guest', 'true');
  } else {
    localStorage.removeItem('cigar_journal_guest');
  }
};

// Guest local storage for notes
const GUEST_NOTES_KEY = 'cigar_journal_guest_notes';
const getGuestNotes = (): TastingNote[] => JSON.parse(localStorage.getItem(GUEST_NOTES_KEY) || '[]');
const saveGuestNotes = (notes: TastingNote[]): void => localStorage.setItem(GUEST_NOTES_KEY, JSON.stringify(notes));

// Fetch wrapper with auth
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const token = getToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  try {
    const res = await fetch(`${API_BASE}${endpoint}`, { ...options, headers });
    return await res.json();
  } catch (error) {
    return { error: 'Network error', status: 500 };
  }
}

// Auth hook
export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = getToken();
      if (!token) {
        setLoading(false);
        return;
      }

      const res = await apiFetch<User>('/auth/me');
      if (res.data) {
        setUser(res.data);
      } else {
        clearToken();
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email: string, password: string): Promise<string | null> => {
    const res = await apiFetch<User & { token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (res.data) {
      setToken(res.data.token);
      setUser(res.data);
      return null;
    }
    return res.error || 'Login failed';
  };

  const loginWithGoogle = async (credential: string): Promise<string | null> => {
    const res = await apiFetch<User & { token: string }>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential }),
    });

    if (res.data) {
      setToken(res.data.token);
      setGuestMode(false);
      setUser(res.data);
      return null;
    }
    return res.error || 'Google sign-in failed';
  };

  const register = async (
    email: string,
    password: string,
    name: string,
    preferences?: object
  ): Promise<string | null> => {
    const res = await apiFetch<User & { token: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name, preferences }),
    });

    if (res.data) {
      setToken(res.data.token);
      setUser(res.data);
      return null;
    }
    return res.error || 'Registration failed';
  };

  const logout = () => {
    clearToken();
    setGuestMode(false);
    setUser(null);
  };

  const continueAsGuest = () => {
    const guestUser: User = {
      id: 'guest',
      email: '',
      name: 'Guest',
      preferences: { strength: 'medium', flavors: [], experience: 'beginner' },
      createdAt: new Date().toISOString(),
    };
    setGuestMode(true);
    setUser(guestUser);
  };

  return { user, loading, login, loginWithGoogle, register, logout, continueAsGuest };
}

// Tasting notes hook
export function useNotes() {
  const [notes, setNotes] = useState<TastingNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNotes = useCallback(async (params?: {
    limit?: number;
    offset?: number;
    sort?: string;
    order?: 'ASC' | 'DESC';
  }) => {
    setLoading(true);
    setError(null);

    // Guest mode: use localStorage
    if (isGuestMode()) {
      const guestNotes = getGuestNotes();
      // Sort by smoke_date descending by default
      guestNotes.sort((a, b) => new Date(b.smokeDate).getTime() - new Date(a.smokeDate).getTime());
      setNotes(guestNotes);
      setLoading(false);
      return;
    }

    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    if (params?.sort) query.set('sort', params.sort);
    if (params?.order) query.set('order', params.order);

    const res = await apiFetch<TastingNote[]>(`/notes?${query}`);

    if (res.data) {
      setNotes(res.data);
    } else {
      setError(res.error || 'Failed to fetch notes');
    }
    setLoading(false);
  }, []);

  const getNote = async (id: string): Promise<TastingNote | null> => {
    // Guest mode: use localStorage
    if (isGuestMode()) {
      const guestNotes = getGuestNotes();
      return guestNotes.find(n => n.id === id) || null;
    }

    const res = await apiFetch<TastingNote>(`/notes/${id}`);
    return res.data || null;
  };

  const createNote = async (note: Partial<TastingNote>): Promise<TastingNote | null> => {
    // Guest mode: use localStorage
    if (isGuestMode()) {
      const now = new Date().toISOString();
      const newNote: TastingNote = {
        id: crypto.randomUUID(),
        userId: 'guest',
        brand: note.brand || '',
        flavors: note.flavors || [],
        wouldBuyAgain: note.wouldBuyAgain || false,
        smokeDate: note.smokeDate || now.split('T')[0],
        isPublic: false,
        createdAt: now,
        updatedAt: now,
        ...note,
      } as TastingNote;

      const guestNotes = getGuestNotes();
      guestNotes.unshift(newNote);
      saveGuestNotes(guestNotes);
      setNotes(guestNotes);
      return newNote;
    }

    const res = await apiFetch<TastingNote>('/notes', {
      method: 'POST',
      body: JSON.stringify(note),
    });

    if (res.data) {
      setNotes(prev => [res.data!, ...prev]);
      return res.data;
    }
    setError(res.error || 'Failed to create note');
    return null;
  };

  const updateNote = async (id: string, note: Partial<TastingNote>): Promise<boolean> => {
    // Guest mode: use localStorage
    if (isGuestMode()) {
      const guestNotes = getGuestNotes();
      const index = guestNotes.findIndex(n => n.id === id);
      if (index === -1) return false;

      guestNotes[index] = { ...guestNotes[index], ...note, updatedAt: new Date().toISOString() };
      saveGuestNotes(guestNotes);
      setNotes(guestNotes);
      return true;
    }

    const res = await apiFetch<TastingNote>(`/notes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(note),
    });

    if (res.data) {
      setNotes(prev => prev.map(n => n.id === id ? res.data! : n));
      return true;
    }
    setError(res.error || 'Failed to update note');
    return false;
  };

  const deleteNote = async (id: string): Promise<boolean> => {
    // Guest mode: use localStorage
    if (isGuestMode()) {
      const guestNotes = getGuestNotes().filter(n => n.id !== id);
      saveGuestNotes(guestNotes);
      setNotes(guestNotes);
      return true;
    }

    const res = await apiFetch<{ success: boolean }>(`/notes/${id}`, {
      method: 'DELETE',
    });

    if (res.data?.success) {
      setNotes(prev => prev.filter(n => n.id !== id));
      return true;
    }
    setError(res.error || 'Failed to delete note');
    return false;
  };

  return { notes, loading, error, fetchNotes, getNote, createNote, updateNote, deleteNote };
}

// Photo upload hook
export function usePhotoUpload() {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const uploadPhoto = async (
    file: File,
    type: 'band' | 'cigar' | 'ash'
  ): Promise<string | null> => {
    setUploading(true);
    setProgress(0);

    // Guest mode: store as data URL in localStorage
    if (isGuestMode()) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          // Store in localStorage with a unique key
          const key = `guest_photo_${type}_${Date.now()}`;
          localStorage.setItem(key, dataUrl);
          setProgress(100);
          setUploading(false);
          resolve(key);
        };
        reader.readAsDataURL(file);
      });
    }

    const formData = new FormData();
    formData.append('photo', file);
    formData.append('type', type);

    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/photos`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      const data = await res.json() as { data?: { key: string } };
      setProgress(100);
      setUploading(false);

      return data.data?.key || null;
    } catch (error) {
      setUploading(false);
      return null;
    }
  };

  return { uploadPhoto, uploading, progress };
}

// Share hook
export function useShare() {
  const createShare = async (
    noteId: string,
    options?: { sharedWith?: string[]; expiresIn?: number }
  ): Promise<Share | null> => {
    const res = await apiFetch<Share>('/shares', {
      method: 'POST',
      body: JSON.stringify({ noteId, ...options }),
    });
    return res.data || null;
  };

  const getShare = async (code: string): Promise<{
    note: TastingNote;
    sharedBy: { name: string; avatar?: string };
    viewCount: number;
  } | null> => {
    const res = await apiFetch<any>(`/shares/${code}`);
    return res.data || null;
  };

  return { createShare, getShare };
}

// Social/friends hook
export function useSocial() {
  const [friends, setFriends] = useState<any[]>([]);
  const [feed, setFeed] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchFriends = async () => {
    setLoading(true);
    const res = await apiFetch<any[]>('/friends');
    if (res.data) setFriends(res.data);
    setLoading(false);
  };

  const addFriend = async (email: string): Promise<boolean> => {
    const res = await apiFetch<{ success: boolean }>('/friends', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    if (res.data?.success) {
      await fetchFriends();
      return true;
    }
    return false;
  };

  const fetchFeed = async () => {
    setLoading(true);
    const res = await apiFetch<Activity[]>('/feed');
    if (res.data) setFeed(res.data);
    setLoading(false);
  };

  return { friends, feed, loading, fetchFriends, addFriend, fetchFeed };
}

// Stats hook
export function useStats() {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const fetchStats = async () => {
    setLoading(true);

    // Guest mode: compute stats from localStorage
    if (isGuestMode()) {
      const notes = getGuestNotes();
      const flavorCounts: Record<string, number> = {};
      const brandCounts: Record<string, { count: number; totalScore: number }> = {};

      let totalScore = 0;
      let scoreCount = 0;
      let totalSpent = 0;
      let totalDuration = 0;

      for (const note of notes) {
        if (note.overallScore) {
          totalScore += note.overallScore;
          scoreCount++;
        }
        if (note.price) totalSpent += note.price;
        if (note.smokeDuration) totalDuration += note.smokeDuration;

        for (const f of note.flavors || []) {
          flavorCounts[f] = (flavorCounts[f] || 0) + 1;
        }

        if (note.brand) {
          if (!brandCounts[note.brand]) {
            brandCounts[note.brand] = { count: 0, totalScore: 0 };
          }
          brandCounts[note.brand].count++;
          if (note.overallScore) {
            brandCounts[note.brand].totalScore += note.overallScore;
          }
        }
      }

      const topBrands = Object.entries(brandCounts)
        .map(([brand, data]) => ({
          brand,
          count: data.count,
          avg_score: data.count > 0 ? Math.round(data.totalScore / data.count) : 0,
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      const topFlavors = Object.entries(flavorCounts)
        .map(([flavor, count]) => ({ flavor, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      setStats({
        totalNotes: notes.length,
        averageScore: scoreCount > 0 ? Math.round((totalScore / scoreCount) * 10) / 10 : 0,
        uniqueBrands: Object.keys(brandCounts).length,
        totalDuration,
        totalSpent,
        topBrands,
        topFlavors,
      });
      setLoading(false);
      return;
    }

    const res = await apiFetch<any>('/stats');
    if (res.data) setStats(res.data);
    setLoading(false);
  };

  return { stats, loading, fetchStats };
}

// Camera hook for photo capture
export function useCamera() {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);

  const startCamera = async (facingMode: 'user' | 'environment' = 'environment') => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      setStream(mediaStream);
      setError(null);
      return mediaStream;
    } catch (err) {
      setError('Camera access denied');
      return null;
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const capturePhoto = (video: HTMLVideoElement): string | null => {
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    
    ctx.drawImage(video, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.85);
  };

  return { stream, error, startCamera, stopCamera, capturePhoto };
}

// PWA Install Prompt hook
export function usePWA() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if running in standalone mode (already installed)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as any).standalone === true;
    setIsInstalled(isStandalone);

    // Detect iOS Safari
    const ua = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(ua) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };

    // Listen for appinstalled event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = async (): Promise<boolean> => {
    if (!installPrompt) return false;

    const result = await installPrompt.prompt();
    const outcome = await result.userChoice;

    if (outcome === 'accepted') {
      setIsInstalled(true);
      setInstallPrompt(null);
      return true;
    }
    return false;
  };

  const canInstall = !isInstalled && (installPrompt !== null || isIOS);

  return { canInstall, isInstalled, isIOS, promptInstall };
}

// BeforeInstallPromptEvent type (not in standard lib)
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<{ userChoice: 'accepted' | 'dismissed' }>;
}

// Offline storage hook (for PWA)
export function useOfflineStorage() {
  const saveDraft = (note: Partial<TastingNote>) => {
    const drafts = JSON.parse(localStorage.getItem('cigar_drafts') || '[]');
    drafts.push({ ...note, draftId: Date.now(), savedAt: new Date().toISOString() });
    localStorage.setItem('cigar_drafts', JSON.stringify(drafts));
  };

  const getDrafts = (): (Partial<TastingNote> & { draftId: number })[] => {
    return JSON.parse(localStorage.getItem('cigar_drafts') || '[]');
  };

  const deleteDraft = (draftId: number) => {
    const drafts = getDrafts().filter(d => d.draftId !== draftId);
    localStorage.setItem('cigar_drafts', JSON.stringify(drafts));
  };

  const clearDrafts = () => {
    localStorage.removeItem('cigar_drafts');
  };

  return { saveDraft, getDrafts, deleteDraft, clearDrafts };
}

// Voice input hook
export { useVoice } from './useVoice';
export type { VoiceState, UseVoiceReturn } from './useVoice';
